import { useMemo, useState } from 'react'
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import axios from 'axios'
import {
  ChevronDown,
  ChevronRight,
  Download,
  Plus,
  RefreshCw,
  Trash2,
  UploadCloud,
  FileText,
  AlertTriangle,
} from 'lucide-react'
import api from '../services/api'
import { useWorkspace } from '../hooks/useWorkspace'
import { useLanguage } from '../hooks/useLanguage'
import Modal, { ModalActions, ModalButton } from '../components/Modal'

interface ChartAccount {
  code: string
  name: string
  parent_code: string | null
  level: number
  section: string
}

interface ChartAccountRule {
  id: string
  pattern: string
  account_code: string
  created_at: string
}

interface SummaryRow extends ChartAccount {
  children_count: number
  actual: number
  forecast_year_1: number
  forecast_year_2: number
  forecast_year_3: number
}

interface SummaryMeta {
  year: number
  accounts: number
  mapped_transactions: number
}

interface ChartAccountsMeta {
  imported_file: string
  imported_at: string
  accounts_count: number
}

const ROOT_KEY = '__root__'

export default function Tabellone() {
  const { currentWorkspace } = useWorkspace()
  const { language } = useLanguage()
  const queryClient = useQueryClient()

  const [selectedYear, setSelectedYear] = useState(new Date().getFullYear())
  const [expandedRows, setExpandedRows] = useState<Set<string>>(new Set())
  const [importFile, setImportFile] = useState<File | null>(null)
  const [rulePattern, setRulePattern] = useState('')
  const [ruleAccountCode, setRuleAccountCode] = useState('')
  const [showDeleteConfirm, setShowDeleteConfirm] = useState(false)

  const isItalian = language === 'it'

  const { data: chartAccountsData, isLoading: loadingAccounts } = useQuery({
    queryKey: ['chart-accounts', currentWorkspace?.id],
    queryFn: async () => {
      const response = await api.get('/chart-accounts')
      return response.data as { data: ChartAccount[]; meta?: ChartAccountsMeta }
    },
    enabled: !!currentWorkspace?.id,
  })

  const { data: rulesData } = useQuery({
    queryKey: ['chart-account-rules', currentWorkspace?.id],
    queryFn: async () => {
      const response = await api.get('/chart-accounts/rules')
      return response.data as { data: ChartAccountRule[] }
    },
    enabled: !!currentWorkspace?.id,
  })

  const { data: summaryData, isLoading: loadingSummary } = useQuery({
    queryKey: ['chart-accounts-summary', currentWorkspace?.id, selectedYear],
    queryFn: async () => {
      const response = await api.get(`/chart-accounts/summary?year=${selectedYear}`)
      return response.data as { data: SummaryRow[]; meta: SummaryMeta }
    },
    enabled: !!currentWorkspace?.id,
  })

  const accounts = useMemo(() => chartAccountsData?.data || [], [chartAccountsData?.data])
  const importMeta = chartAccountsData?.meta
  const rules = useMemo(() => rulesData?.data || [], [rulesData?.data])
  const summaryRows = useMemo(() => summaryData?.data || [], [summaryData?.data])
  const summaryMeta = summaryData?.meta

  const importMutation = useMutation({
    mutationFn: async (file: File) => {
      const formData = new FormData()
      formData.append('file', file)
      const response = await api.post('/chart-accounts/import', formData, {
        headers: { 'Content-Type': 'multipart/form-data' },
      })
      return response.data as { imported: number; message: string }
    },
    onSuccess: () => {
      setImportFile(null)
      queryClient.invalidateQueries({ queryKey: ['chart-accounts'] })
      queryClient.invalidateQueries({ queryKey: ['chart-accounts-summary'] })
    },
  })

  const importErrorMessage = useMemo(() => {
    if (!importMutation.isError || !importMutation.error) return null
    if (axios.isAxiosError(importMutation.error)) {
      const payload = importMutation.error.response?.data as { error?: string } | undefined
      return payload?.error || (isItalian ? 'Import non riuscito' : 'Import failed')
    }
    return isItalian ? 'Import non riuscito' : 'Import failed'
  }, [importMutation.error, importMutation.isError, isItalian])

  const mapExistingMutation = useMutation({
    mutationFn: async () => {
      const response = await api.post('/chart-accounts/map-existing')
      return response.data as { updated_files: number; updated_transactions: number }
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['transactions'] })
      queryClient.invalidateQueries({ queryKey: ['chart-accounts-summary'] })
    },
  })

  const createRuleMutation = useMutation({
    mutationFn: async (payload: { pattern: string; account_code: string }) => {
      const response = await api.post('/chart-accounts/rules', payload)
      return response.data
    },
    onSuccess: () => {
      setRulePattern('')
      queryClient.invalidateQueries({ queryKey: ['chart-account-rules'] })
      queryClient.invalidateQueries({ queryKey: ['chart-accounts-summary'] })
    },
  })

  const deleteRuleMutation = useMutation({
    mutationFn: async (ruleId: string) => {
      await api.delete(`/chart-accounts/rules/${ruleId}`)
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['chart-account-rules'] })
      queryClient.invalidateQueries({ queryKey: ['chart-accounts-summary'] })
    },
  })

  const deleteChartMutation = useMutation({
    mutationFn: async () => {
      await api.delete('/chart-accounts')
    },
    onSuccess: () => {
      setShowDeleteConfirm(false)
      queryClient.invalidateQueries({ queryKey: ['chart-accounts'] })
      queryClient.invalidateQueries({ queryKey: ['chart-account-rules'] })
      queryClient.invalidateQueries({ queryKey: ['chart-accounts-summary'] })
    },
  })

  const rowsByParent = useMemo(() => {
    const map = new Map<string, SummaryRow[]>()
    const byCode = new Set(summaryRows.map((row) => row.code))

    for (const row of summaryRows) {
      const parentKey = row.parent_code && byCode.has(row.parent_code) ? row.parent_code : ROOT_KEY
      const bucket = map.get(parentKey) || []
      bucket.push(row)
      map.set(parentKey, bucket)
    }

    for (const [key, bucket] of map.entries()) {
      bucket.sort((a, b) => a.code.localeCompare(b.code))
      map.set(key, bucket)
    }

    return map
  }, [summaryRows])

  const visibleRows = useMemo(() => {
    const output: SummaryRow[] = []

    const walk = (parentCode: string) => {
      const children = rowsByParent.get(parentCode) || []
      for (const child of children) {
        output.push(child)
        if (expandedRows.has(child.code)) {
          walk(child.code)
        }
      }
    }

    walk(ROOT_KEY)
    return output
  }, [expandedRows, rowsByParent])

  const formatCurrency = (value: number) =>
    new Intl.NumberFormat(isItalian ? 'it-IT' : 'en-US', { style: 'currency', currency: 'EUR' }).format(value)

  const allExpandableCodes = useMemo(
    () => summaryRows.filter((row) => row.children_count > 0).map((row) => row.code),
    [summaryRows],
  )

  const isAllExpanded = allExpandableCodes.length > 0 && allExpandableCodes.every((code) => expandedRows.has(code))

  const handleImport = () => {
    if (!importFile) return
    importMutation.mutate(importFile)
  }

  const handleCreateRule = () => {
    const pattern = rulePattern.trim()
    if (!pattern || !ruleAccountCode) return
    createRuleMutation.mutate({
      pattern,
      account_code: ruleAccountCode,
    })
  }

  return (
    <div className="space-y-6 pt-4">
      <div className="flex flex-col sm:flex-row sm:items-start sm:justify-between gap-4">
        <div className="space-y-1">
          <div className="flex items-center gap-3">
            {/* Page title now in top bar */}
          </div>
          <p className="text-gray-600 dark:text-gray-400 text-sm mt-2">
            {isItalian
              ? 'Consuntivo e forecast multi-anno per conto/sottoconto, con mapping automatico e override manuale.'
              : 'Actuals and multi-year forecast by account/subaccount, with automatic mapping and manual overrides.'}
          </p>
          {importMeta && (
            <div className="flex items-center gap-3 mt-2 text-xs text-gray-500 dark:text-gray-400">
              <FileText className="w-3.5 h-3.5 flex-shrink-0" />
              <span className="truncate">
                {isItalian ? 'Importato da' : 'Imported from'}: <strong>{importMeta.imported_file}</strong>
              </span>
              <span>•</span>
              <span>{new Date(importMeta.imported_at).toLocaleDateString(isItalian ? 'it-IT' : 'en-US')}</span>
              <span>•</span>
              <span>{importMeta.accounts_count} {isItalian ? 'conti' : 'accounts'}</span>
            </div>
          )}
        </div>

        <div className="flex items-center gap-2">
          <button
            onClick={() => {
              if (isAllExpanded) {
                setExpandedRows(new Set())
              } else {
                setExpandedRows(new Set(allExpandableCodes))
              }
            }}
            className="btn btn-secondary"
            disabled={summaryRows.length === 0}
          >
            {isAllExpanded
              ? (isItalian ? 'Comprimi tutto' : 'Collapse all')
              : (isItalian ? 'Espandi tutto' : 'Expand all')}
          </button>
          <button
            onClick={() => mapExistingMutation.mutate()}
            className="btn btn-primary flex items-center gap-2"
            disabled={mapExistingMutation.isPending || accounts.length === 0}
          >
            <RefreshCw className={`w-4 h-4 ${mapExistingMutation.isPending ? 'animate-spin' : ''}`} />
            {isItalian ? 'Remap Storico' : 'Remap Existing'}
          </button>
          {accounts.length > 0 && (
            <button
              onClick={() => setShowDeleteConfirm(true)}
              className="btn btn-secondary text-red-600 dark:text-red-400 hover:bg-red-50 dark:hover:bg-red-900/20 flex items-center gap-2"
            >
              <Trash2 className="w-4 h-4" />
              {isItalian ? 'Elimina' : 'Delete'}
            </button>
          )}
        </div>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
        <div className="card">
          <p className="text-xs font-medium uppercase tracking-[0.08em] text-slate-400">{isItalian ? 'Conti Tecnici' : 'Accounts'}</p>
          <p className="kpi-value kpi-animate text-gray-900 dark:text-white mt-1">{accounts.length}</p>
        </div>
        <div className="card">
          <p className="text-xs font-medium uppercase tracking-[0.08em] text-slate-400">{isItalian ? 'Regole Mapping' : 'Mapping Rules'}</p>
          <p className="kpi-value kpi-animate text-gray-900 dark:text-white mt-1">{rules.length}</p>
        </div>
        <div className="card">
          <p className="text-xs font-medium uppercase tracking-[0.08em] text-slate-400">{isItalian ? 'Transazioni mappate' : 'Mapped transactions'}</p>
          <p className="kpi-value kpi-animate text-gray-900 dark:text-white mt-1">{summaryMeta?.mapped_transactions || 0}</p>
        </div>
      </div>

      <div className="grid grid-cols-1 xl:grid-cols-5 gap-6">
        <div className="card xl:col-span-2 space-y-4">
          <div>
            <h3 className="text-base font-semibold text-gray-900 dark:text-white">
              {isItalian ? 'Import Piano dei Conti' : 'Import Chart of Accounts'}
            </h3>
            <p className="text-xs text-gray-500 dark:text-gray-400 mt-1">
              PDF, CSV, XLS, XLSX
            </p>
          </div>

          <label className="block">
            <input
              type="file"
              accept=".pdf,.csv,.xls,.xlsx"
              onChange={(e) => setImportFile(e.target.files?.[0] || null)}
              className="block w-full text-sm text-gray-700 dark:text-gray-300 file:mr-3 file:rounded-lg file:border-0 file:bg-primary-100 file:px-3 file:py-2 file:text-primary-700 file:cursor-pointer"
            />
          </label>

          {importErrorMessage && (
            <p className="text-sm text-red-600 dark:text-red-400">{importErrorMessage}</p>
          )}

          {importMutation.isSuccess && (
            <p className="text-sm text-green-600 dark:text-green-400">
              {isItalian ? 'Import completato con successo.' : 'Import completed successfully.'}
            </p>
          )}

          <button
            onClick={handleImport}
            className="btn btn-primary w-full flex items-center justify-center gap-2"
            disabled={!importFile || importMutation.isPending}
          >
            <UploadCloud className={`w-4 h-4 ${importMutation.isPending ? 'animate-pulse' : ''}`} />
            {importMutation.isPending
              ? (isItalian ? 'Import in corso...' : 'Importing...')
              : (isItalian ? 'Importa Piano dei Conti' : 'Import Chart of Accounts')}
          </button>

          <button
            onClick={() => {
              const csvLines = [
                'code;name;parent_code;level;section',
                '100;Ricavi; ;1;ricavi',
                '100.01;Ricavi servizi;100;2;ricavi',
                '200;Costi; ;1;costi',
                '200.01;Costi software;200;2;costi',
              ]
              const blob = new Blob([csvLines.join('\n')], { type: 'text/csv;charset=utf-8;' })
              const url = URL.createObjectURL(blob)
              const link = document.createElement('a')
              link.href = url
              link.download = 'piano_dei_conti_template.csv'
              document.body.appendChild(link)
              link.click()
              document.body.removeChild(link)
              URL.revokeObjectURL(url)
            }}
            className="btn btn-secondary w-full flex items-center justify-center gap-2"
          >
            <Download className="w-4 h-4" />
            {isItalian ? 'Scarica Template CSV' : 'Download CSV Template'}
          </button>
        </div>

        <div className="card xl:col-span-3 space-y-4">
          <div>
            <h3 className="text-base font-semibold text-gray-900 dark:text-white">
              {isItalian ? 'Regole Mapping Transazioni/Fatture' : 'Transaction/Invoice Mapping Rules'}
            </h3>
            <p className="text-xs text-gray-500 dark:text-gray-400 mt-1">
              {isItalian
                ? 'Pattern testo descrizione -> conto tecnico'
                : 'Description text pattern -> technical account'}
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-[1fr_220px_auto] gap-2">
            <input
              type="text"
              value={rulePattern}
              onChange={(e) => setRulePattern(e.target.value)}
              placeholder={isItalian ? 'es. openai, canone, enel, stipendio...' : 'e.g. openai, rent, utility, salary...'}
              className="px-3 py-2 border border-gray-300 dark:border-slate-600 rounded-lg bg-white dark:bg-slate-800 text-gray-900 dark:text-white text-sm"
            />

            <select
              value={ruleAccountCode}
              onChange={(e) => setRuleAccountCode(e.target.value)}
              className="px-3 py-2 border border-gray-300 dark:border-slate-600 rounded-lg bg-white dark:bg-slate-800 text-gray-900 dark:text-white text-sm"
            >
              <option value="">{isItalian ? 'Seleziona conto' : 'Select account'}</option>
              {accounts.map((account) => (
                <option key={account.code} value={account.code}>
                  {account.code} - {account.name}
                </option>
              ))}
            </select>

            <button
              onClick={handleCreateRule}
              disabled={!rulePattern.trim() || !ruleAccountCode || createRuleMutation.isPending}
              className="btn btn-primary flex items-center justify-center gap-2"
            >
              <Plus className="w-4 h-4" />
              {isItalian ? 'Aggiungi' : 'Add'}
            </button>
          </div>

          <div className="max-h-64 overflow-auto border border-gray-200 dark:border-slate-700 rounded-xl">
            <table className="table min-w-full">
              <thead>
                <tr>
                  <th className="w-[40%]">Pattern</th>
                  <th>{isItalian ? 'Conto tecnico' : 'Account'}</th>
                  <th className="w-16" />
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-100 dark:divide-slate-700">
                {rules.length === 0 ? (
                  <tr>
                    <td colSpan={3} className="text-center py-6 text-gray-500 dark:text-gray-400">
                      {isItalian ? 'Nessuna regola configurata' : 'No rules configured'}
                    </td>
                  </tr>
                ) : (
                  rules.map((rule) => (
                    <tr key={rule.id}>
                      <td className="font-mono text-xs">{rule.pattern}</td>
                      <td className="font-medium">{rule.account_code}</td>
                      <td>
                        <button
                          onClick={() => deleteRuleMutation.mutate(rule.id)}
                          className="p-2 text-gray-400 hover:text-red-600 rounded-lg hover:bg-red-50 dark:hover:bg-red-900/20"
                          title={isItalian ? 'Elimina regola' : 'Delete rule'}
                        >
                          <Trash2 className="w-4 h-4" />
                        </button>
                      </td>
                    </tr>
                  ))
                )}
              </tbody>
            </table>
          </div>
        </div>
      </div>

      <div className="card p-0 overflow-hidden">
        <div className="flex items-center justify-between px-4 py-3 border-b border-gray-100 dark:border-slate-700">
          <h3 className="text-base font-semibold text-gray-900 dark:text-white">
            {isItalian ? 'Piano dei Conti: Consuntivo + Forecast' : 'Chart of Accounts: Actuals + Forecast'}
          </h3>

          <div className="flex items-center gap-2">
            <label className="text-sm text-gray-600 dark:text-gray-300">
              {isItalian ? 'Anno base' : 'Base year'}
            </label>
            <select
              value={selectedYear}
              onChange={(e) => setSelectedYear(Number(e.target.value))}
              className="px-3 py-2 border border-gray-300 dark:border-slate-600 rounded-lg bg-white dark:bg-slate-800 text-gray-900 dark:text-white text-sm"
            >
              {[0, 1, 2, 3].map((offset) => {
                const year = new Date().getFullYear() - offset
                return (
                  <option key={year} value={year}>
                    {year}
                  </option>
                )
              })}
            </select>
          </div>
        </div>

        <div className="table-container">
          <table className="table min-w-full">
            <thead>
              <tr>
                <th>{isItalian ? 'Conto' : 'Account'}</th>
                <th>{isItalian ? 'Sezione' : 'Section'}</th>
                <th className="text-right">{isItalian ? `Consuntivo ${selectedYear}` : `Actual ${selectedYear}`}</th>
                <th className="text-right">{isItalian ? `Forecast ${selectedYear + 1}` : `Forecast ${selectedYear + 1}`}</th>
                <th className="text-right">{isItalian ? `Forecast ${selectedYear + 2}` : `Forecast ${selectedYear + 2}`}</th>
                <th className="text-right">{isItalian ? `Forecast ${selectedYear + 3}` : `Forecast ${selectedYear + 3}`}</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-100 dark:divide-slate-700">
              {loadingAccounts || loadingSummary ? (
                <tr>
                  <td colSpan={6} className="py-8 text-center">
                    <div className="spinner mx-auto" />
                  </td>
                </tr>
              ) : visibleRows.length === 0 ? (
                <tr>
                  <td colSpan={6} className="py-10 text-center text-gray-500 dark:text-gray-400">
                    {isItalian
                      ? 'Nessun conto disponibile. Importa prima il piano dei conti.'
                      : 'No accounts available yet. Import a chart of accounts first.'}
                  </td>
                </tr>
              ) : (
                visibleRows.map((row) => {
                  const isParent = row.children_count > 0
                  const isExpanded = expandedRows.has(row.code)

                  return (
                    <tr key={row.code} className={isParent ? 'bg-gray-50/60 dark:bg-slate-800/40' : ''}>
                      <td>
                        <div className="flex items-center gap-2" style={{ paddingLeft: `${Math.max(0, row.level - 1) * 18}px` }}>
                          {isParent ? (
                            <button
                              onClick={() => {
                                setExpandedRows((prev) => {
                                  const next = new Set(prev)
                                  if (next.has(row.code)) {
                                    next.delete(row.code)
                                  } else {
                                    next.add(row.code)
                                  }
                                  return next
                                })
                              }}
                              className="p-1 rounded hover:bg-gray-200/70 dark:hover:bg-slate-700"
                              aria-label={isExpanded ? 'Collapse' : 'Expand'}
                            >
                              {isExpanded ? <ChevronDown className="w-4 h-4" /> : <ChevronRight className="w-4 h-4" />}
                            </button>
                          ) : (
                            <span className="w-6" />
                          )}

                          <div className="min-w-0">
                            <p className="font-mono text-xs text-gray-500 dark:text-gray-400">{row.code}</p>
                            <p className="text-sm font-medium text-gray-900 dark:text-gray-100 truncate">{row.name}</p>
                          </div>
                        </div>
                      </td>

                      <td>
                        <span className="text-xs px-2 py-1 rounded-full bg-slate-100 text-slate-700 dark:bg-slate-700 dark:text-slate-200 uppercase">
                          {row.section}
                        </span>
                      </td>

                      <td className={`text-right font-medium ${row.actual < 0 ? 'text-red-600' : 'text-gray-900 dark:text-gray-100'}`}>
                        {formatCurrency(row.actual)}
                      </td>
                      <td className={`text-right ${row.forecast_year_1 < 0 ? 'text-red-600' : 'text-gray-900 dark:text-gray-100'}`}>
                        {formatCurrency(row.forecast_year_1)}
                      </td>
                      <td className={`text-right ${row.forecast_year_2 < 0 ? 'text-red-600' : 'text-gray-900 dark:text-gray-100'}`}>
                        {formatCurrency(row.forecast_year_2)}
                      </td>
                      <td className={`text-right ${row.forecast_year_3 < 0 ? 'text-red-600' : 'text-gray-900 dark:text-gray-100'}`}>
                        {formatCurrency(row.forecast_year_3)}
                      </td>
                    </tr>
                  )
                })
              )}
            </tbody>
          </table>
        </div>
      </div>
      {/* Delete Confirmation Modal */}
      <Modal
        isOpen={showDeleteConfirm}
        onClose={() => setShowDeleteConfirm(false)}
        title={isItalian ? 'Elimina Piano dei Conti' : 'Delete Chart of Accounts'}
      >
        <div className="flex items-start gap-3 mb-4">
          <AlertTriangle className="w-6 h-6 text-amber-500 flex-shrink-0 mt-0.5" />
          <p className="text-sm text-gray-600 dark:text-gray-300">
            {isItalian
              ? 'Questa azione eliminerà il piano dei conti, tutte le regole di mapping e i dati di riepilogo. Le transazioni originali non verranno toccate. Vuoi procedere?'
              : 'This will delete the chart of accounts, all mapping rules and summary data. Original transactions will not be affected. Proceed?'}
          </p>
        </div>
        <ModalActions>
          <ModalButton variant="secondary" onClick={() => setShowDeleteConfirm(false)}>
            {isItalian ? 'Annulla' : 'Cancel'}
          </ModalButton>
          <ModalButton
            variant="danger"
            onClick={() => deleteChartMutation.mutate()}
            disabled={deleteChartMutation.isPending}
          >
            {deleteChartMutation.isPending
              ? (isItalian ? 'Eliminazione...' : 'Deleting...')
              : (isItalian ? 'Elimina tutto' : 'Delete all')}
          </ModalButton>
        </ModalActions>
      </Modal>
    </div>
  )
}
